import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';

import { FileManagerService } from './services/file-manager.service';
import { FileItem } from './models/file-manager.model';
import { ToastService } from '../../shared/services/toast-service';
import { ModalService } from '../../shared/components/modal/modal.service';
import { Modal } from '../../shared/components/modal/modal';
import { ModalFooterDirective } from '../../shared/components/modal/modal-footer.directive';
import { TextInput } from '../../shared/components/forms/text-input';
import { FormField } from '../../shared/components/forms/form-field';
import { Icon } from '../../shared/components/icon/icon';
import { Button } from '../../shared/components/button/button';
import { SegmentedControl } from '../../shared/components/forms/segmented-control';
import { GridPage } from '../../shared/components/grid/grid-page';

/** A crumb in the folder path. `id: null` is the library root. */
interface BreadcrumbEntry {
  id: string | null;
  name: string;
}

/** One rendered breadcrumb slot: either a real crumb, or the collapsed `…` marker. */
type BreadcrumbSlot =
  | { kind: 'crumb'; index: number; entry: BreadcrumbEntry }
  | { kind: 'collapsed'; entries: { index: number; entry: BreadcrumbEntry }[] };

/**
 * Media Library — the organization's file/media browser, rebuilt on the
 * Papercast CMS design language: a grid-page header, a search + view toolbar,
 * a token-styled card grid (thumbnail-first, since this is a media library),
 * breadcrumb folder navigation, and shared `app-modal` dialogs for upload /
 * rename / new-folder (replacing the old hand-rolled backdrops + options menu).
 *
 * Data model and endpoints are unchanged — this is a chrome + interaction
 * redesign over the existing FileManagerService.
 */
@Component({
  selector: 'app-file-manager',
  standalone: true,
  imports: [
    Button,
    FormsModule,
    DatePipe,
    Modal,
    ModalFooterDirective,
    TextInput,
    FormField,
    Icon,
    SegmentedControl,
    GridPage,
    TranslocoDirective,
  ],
  templateUrl: './file-manager.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FileManagerComponent implements OnInit, OnDestroy {
  private readonly fileManagerService = inject(FileManagerService);
  private readonly toastService = inject(ToastService);
  private readonly modal = inject(ModalService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly transloco = inject(TranslocoService);

  readonly items = signal<FileItem[]>([]);
  readonly breadcrumb = signal<BreadcrumbEntry[]>([{ id: null, name: 'Media Library' }]);
  readonly page = signal(0);
  readonly pageSize = signal(20);
  readonly pageSizeOptions = [12, 20, 40, 60];
  readonly totalPages = signal(0);
  readonly totalElements = signal(0);
  readonly isLoading = signal(false);
  readonly query = signal('');

  readonly viewMode = signal<'grid' | 'list'>('grid');

  // Upload dialog state
  readonly pendingUploadFile = signal<File | null>(null);
  readonly isUploadOpen = signal(false);
  uploadName = '';
  uploadDescription = '';

  // New-folder dialog state
  readonly isCreatingFolder = signal(false);
  newFolderName = '';

  // Rename dialog state
  readonly isRenaming = signal(false);
  readonly renameTarget = signal<FileItem | null>(null);
  renameValue = '';

  /**
   * Resolved thumbnail blob-URLs keyed by item id. Signal-owned so the template
   * reads it purely; an `effect` (see constructor) drives fetches off the
   * visible items and revokes URLs for items that scroll out of the set, so the
   * map can't grow unbounded as the user browses folders. An empty-string value
   * is an in-flight marker (fetch started, not yet resolved).
   */
  private readonly thumbnails = signal<Record<string, string>>({});

  constructor() {
    // Fetch thumbnails for the loaded folder's images, and revoke any blob URLs
    // no longer referenced by it. Keyed off `items()` (the loaded folder), not
    // the search-filtered view, so typing in search doesn't churn thumbnails.
    // Runs off item changes (navigation, paging) rather than the render path.
    effect(() => {
      const items = this.items();
      const visible = new Set(items.map((i) => i.id));
      const current = this.thumbnails();

      // Revoke + drop entries for items that are no longer visible.
      const stale = Object.keys(current).filter((id) => !visible.has(id));
      if (stale.length) {
        this.thumbnails.update((map) => {
          const next = { ...map };
          for (const id of stale) {
            if (next[id]) URL.revokeObjectURL(next[id]);
            delete next[id];
          }
          return next;
        });
      }

      // Kick off fetches for images we haven't started yet.
      for (const item of items) {
        if (this.isImage(item) && !(item.id in this.thumbnails())) {
          this.loadThumbnail(item);
        }
      }
    });
  }

  /** The folder whose contents are shown (last crumb). */
  readonly currentDirectoryId = computed(() => {
    const trail = this.breadcrumb();
    return trail[trail.length - 1].id;
  });

  /** Client-side name filter over the current folder's items. */
  readonly filteredItems = computed(() => {
    const q = this.query().trim().toLowerCase();
    const items = this.items();
    return q ? items.filter((i) => i.name.toLowerCase().includes(q)) : items;
  });

  // ── Loading states (same contract as the shared grid) ─────────────────

  /** Cold load: loading with nothing on screen → skeleton cards/rows. */
  readonly showSkeleton = computed(() => this.isLoading() && this.items().length === 0);
  /** Folder navigation / paging over existing content: keep it, dimmed,
   *  until the new folder page lands. No layout jump. */
  readonly isRefetching = computed(() => this.isLoading() && this.items().length > 0);
  /** Bone count: two grid rows / a screenful of list rows. */
  readonly skeletonItems = Array.from({ length: 8 });

  /**
   * Breadcrumb capped at four slots: root / … / parent / current. Deeper
   * trails collapse the skipped ancestors into a `…` slot that opens a menu.
   */
  readonly breadcrumbSlots = computed<BreadcrumbSlot[]>(() => {
    const trail = this.breadcrumb();
    if (trail.length <= 4) {
      return trail.map((entry, index) => ({ kind: 'crumb' as const, index, entry }));
    }
    const root = { kind: 'crumb' as const, index: 0, entry: trail[0] };
    const parent = {
      kind: 'crumb' as const,
      index: trail.length - 2,
      entry: trail[trail.length - 2],
    };
    const current = {
      kind: 'crumb' as const,
      index: trail.length - 1,
      entry: trail[trail.length - 1],
    };
    const collapsed = {
      kind: 'collapsed' as const,
      entries: trail.slice(1, trail.length - 2).map((entry, i) => ({ index: i + 1, entry })),
    };
    return [root, collapsed, parent, current];
  });

  readonly isBreadcrumbMenuOpen = signal(false);

  /** "(page*pageSize + 1)–(page*pageSize + shown) / total" range label. */
  readonly rangeLabel = computed(() => {
    const total = this.totalElements();
    if (total === 0) return '0';
    const shown = this.filteredItems().length;
    const from = this.page() * this.pageSize() + 1;
    const to = this.page() * this.pageSize() + shown;
    return `${from}–${to} / ${total}`;
  });

  ngOnInit(): void {
    this.fetchContents();
  }

  ngOnDestroy(): void {
    Object.values(this.thumbnails()).forEach((url) => url && URL.revokeObjectURL(url));
  }

  fetchContents(): void {
    this.isLoading.set(true);
    const id = this.currentDirectoryId();
    const obs =
      id === null
        ? this.fileManagerService.getRootContents(this.page(), this.pageSize())
        : this.fileManagerService.getDirectoryContents(id, this.page(), this.pageSize());

    obs.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (response) => {
        this.items.set(response.data);
        this.totalPages.set(response.totalPages);
        this.totalElements.set(response.totalElements);
        this.isLoading.set(false);
      },
      error: () => {
        this.toastService.show(this.transloco.translate('fileManager.toast.loadFailed'), 'error');
        this.isLoading.set(false);
      },
    });
  }

  // ── Navigation ─────────────────────────────────────────────────────────

  navigateInto(item: FileItem): void {
    if (item.type !== 'FOLDER') return;
    this.breadcrumb.update((trail) => [...trail, { id: item.id, name: item.name }]);
    this.page.set(0);
    this.query.set('');
    this.fetchContents();
  }

  navigateTo(index: number): void {
    this.breadcrumb.update((trail) => trail.slice(0, index + 1));
    this.page.set(0);
    this.query.set('');
    this.fetchContents();
  }

  /**
   * Single-click on a card/row descends into folders. Files do nothing on a
   * body click — they're acted on via the explicit Download/Rename/Delete
   * controls, so a stray click can't trigger an accidental download.
   */
  onItemActivate(item: FileItem): void {
    if (item.type === 'FOLDER') this.navigateInto(item);
  }

  // ── Row / card actions ───────────────────────────────────────────────

  openRename(item: FileItem): void {
    this.renameTarget.set(item);
    this.renameValue = item.name;
    this.isRenaming.set(true);
  }

  submitRename(): void {
    const target = this.renameTarget();
    const name = this.renameValue.trim();
    if (!target || !name) return;
    this.fileManagerService
      .renameItem(target.id, { name })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.toastService.show(this.transloco.translate('fileManager.toast.renamed'), 'success');
          this.closeRename();
          this.fetchContents();
        },
        error: () => this.toastService.show(this.transloco.translate('fileManager.toast.renameFailed'), 'error'),
      });
  }

  closeRename(): void {
    this.isRenaming.set(false);
    this.renameTarget.set(null);
    this.renameValue = '';
  }

  async confirmDelete(item: FileItem): Promise<void> {
    const confirmed = await this.modal.confirm({
      kind: 'destructive',
      destructive: true,
      title: this.transloco.translate('fileManager.delete.title'),
      message: this.transloco.translate('fileManager.delete.message', { name: item.name }),
      confirmLabel: this.transloco.translate('fileManager.delete.confirm'),
    });
    if (confirmed) this.deleteItem(item.id);
  }

  private deleteItem(id: string): void {
    this.fileManagerService
      .deleteItem(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.toastService.show(this.transloco.translate('fileManager.toast.deleted'), 'success');
          this.fetchContents();
        },
        error: () => this.toastService.show(this.transloco.translate('fileManager.toast.deleteFailed'), 'error'),
      });
  }

  downloadItem(item: FileItem): void {
    if (item.type !== 'FILE') return;
    this.fileManagerService
      .downloadFile(item.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (blob) => {
          const url = URL.createObjectURL(blob);
          const anchor = document.createElement('a');
          anchor.href = url;
          anchor.download = item.name;
          anchor.click();
          URL.revokeObjectURL(url);
        },
        error: () => this.toastService.show(this.transloco.translate('fileManager.toast.downloadFailed'), 'error'),
      });
  }

  // ── Upload ───────────────────────────────────────────────────────────

  onFileInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;
    const file = input.files[0];
    this.pendingUploadFile.set(file);
    this.uploadName = file.name;
    this.uploadDescription = '';
    this.isUploadOpen.set(true);
    input.value = '';
  }

  submitUpload(): void {
    const file = this.pendingUploadFile();
    if (!file || !this.uploadName.trim()) return;
    this.fileManagerService
      .uploadFile(
        file,
        this.currentDirectoryId(),
        this.uploadName.trim() || undefined,
        this.uploadDescription.trim() || undefined,
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.toastService.show(this.transloco.translate('fileManager.toast.uploaded'), 'success');
          this.closeUpload();
          this.fetchContents();
        },
        error: () => this.toastService.show(this.transloco.translate('fileManager.toast.uploadFailed'), 'error'),
      });
  }

  closeUpload(): void {
    this.isUploadOpen.set(false);
    this.pendingUploadFile.set(null);
    this.uploadName = '';
    this.uploadDescription = '';
  }

  // ── New folder ─────────────────────────────────────────────────────────

  openCreateFolder(): void {
    this.newFolderName = '';
    this.isCreatingFolder.set(true);
  }

  submitCreateFolder(): void {
    const name = this.newFolderName.trim();
    if (!name) return;
    this.fileManagerService
      .createDirectory({ name, parentId: this.currentDirectoryId() })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.toastService.show(this.transloco.translate('fileManager.toast.folderCreated'), 'success');
          this.closeCreateFolder();
          this.fetchContents();
        },
        error: () => this.toastService.show(this.transloco.translate('fileManager.toast.folderCreateFailed'), 'error'),
      });
  }

  closeCreateFolder(): void {
    this.isCreatingFolder.set(false);
    this.newFolderName = '';
  }

  // ── Pagination ─────────────────────────────────────────────────────────

  prevPage(): void {
    if (this.page() === 0) return;
    this.page.update((p) => p - 1);
    this.fetchContents();
  }

  nextPage(): void {
    if (this.page() >= this.totalPages() - 1) return;
    this.page.update((p) => p + 1);
    this.fetchContents();
  }

  setPageSize(size: number): void {
    if (size === this.pageSize()) return;
    this.pageSize.set(size);
    this.page.set(0);
    this.fetchContents();
  }

  toggleBreadcrumbMenu(): void {
    this.isBreadcrumbMenuOpen.update((open) => !open);
  }

  navigateToCollapsed(index: number): void {
    this.isBreadcrumbMenuOpen.set(false);
    this.navigateTo(index);
  }

  // ── Rendering helpers ────────────────────────────────────────────────

  isImage(item: FileItem): boolean {
    return !!item.contentType?.startsWith('image/');
  }

  /** Pure read of a resolved thumbnail URL (empty while pending/absent). */
  thumbnailUrl(item: FileItem): string {
    return this.thumbnails()[item.id] ?? '';
  }

  /** True while an image item's thumbnail blob is still being fetched —
   *  drives a pulse placeholder instead of an empty `<img src="">`. */
  thumbnailPending(item: FileItem): boolean {
    return this.isImage(item) && !this.thumbnails()[item.id];
  }

  /** Fetch one thumbnail and fold the resulting blob URL into the signal map. */
  private loadThumbnail(item: FileItem): void {
    // In-flight marker so the effect doesn't re-dispatch on the next run.
    this.thumbnails.update((map) => ({ ...map, [item.id]: '' }));
    this.fileManagerService
      .downloadThumbnail(item.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((blob) => {
        const url = URL.createObjectURL(blob);
        // Guard against a race: the item may have been evicted while in flight.
        // If so, revoke immediately rather than orphan the blob URL.
        if (!(item.id in this.thumbnails())) {
          URL.revokeObjectURL(url);
          return;
        }
        this.thumbnails.update((map) => ({ ...map, [item.id]: url }));
      });
  }

  formatSize(bytes: number | null): string {
    if (bytes === null) return '—';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  }
}
